// Criterios de presentación del listado "Para aprobar": secciones, colores y orden. Puro (sin
// React) para poder probarlo; la página y el modal lo usan.

/**
 * Las secciones destacadas, en el orden en que van arriba del listado. Los colores son los que
 * pidió el estudio para el sistema (oct-2026): Favorable verde, Desfavorable rojo, Citado amarillo.
 * OJO: la planilla usaba otra convención (amarillo = desfavorable, verde = citado).
 */
export const CATEGORIAS = {
    // `accion`: el botón de la fila. Dice qué se hace, no "confirmar" a secas (no se entendía).
    FAVORABLE: { etiqueta: 'Favorables — pasar a Favorables', accion: 'Pasar a Favorables', color: '#15803d', colorTexto: '#fff' },
    DESFAVORABLE: { etiqueta: 'Desfavorables — decidir cómo sigue', accion: 'Decidir cómo sigue', color: '#b91c1c', colorTexto: '#fff' },
    CITADO: { etiqueta: 'Citados — cargar la citación', accion: 'Cargar citación', color: '#facc15', colorTexto: '#422006' },
    // El robot dejó de verlo en Mi ANSES (ni en curso ni cerrado): hay que mirarlo a mano.
    DESAPARECIDO: { etiqueta: 'Ya no figuran en Mi ANSES — revisar', accion: 'Ya lo revisé', color: '#6d28d9', colorTexto: '#fff' },
    ACTUALIZACION: { etiqueta: 'Actualizaciones de ANSES (últimos 7 días)', color: '#2563eb', colorTexto: '#fff' },
    REQUIERE_CONTROL: { etiqueta: 'Requieren control manual', color: '#c2410c', colorTexto: '#fff' },
};

export const ORDEN_CATEGORIAS = ['FAVORABLE', 'DESFAVORABLE', 'CITADO', 'DESAPARECIDO', 'ACTUALIZACION', 'REQUIERE_CONTROL'];

/** Cómo se ve el estado en ANSES en la fila (pastilla de color). */
export const ESTADOS_ANSES = {
    FAVORABLE: { etiqueta: 'Favorable', bg: '#dcfce7', fg: '#166534', borde: '#86efac' },
    DESFAVORABLE: { etiqueta: 'Desfavorable', bg: '#fee2e2', fg: '#991b1b', borde: '#fca5a5' },
    CITADO: { etiqueta: 'Citado', bg: '#fef9c3', fg: '#854d0e', borde: '#fde047' },
    EN_CURSO: { etiqueta: 'En curso', bg: '#f1f5f9', fg: '#334155', borde: '#6b7280' },
    // Solo lo marca el robot: no se elige al registrar un control a mano.
    DESAPARECIDO: { etiqueta: 'No figura en Mi ANSES', bg: '#ede9fe', fg: '#5b21b6', borde: '#c4b5fd', soloRobot: true },
};

export const ORIGEN_CONTROL = {
    AUTOMATICO: 'Robot ANSES',
    MANUAL: 'Manual',
    MIGRACION: 'Planilla',
};

/**
 * Las filas en el orden del listado: primero las secciones destacadas (en ORDEN_CATEGORIAS) y
 * después cada tipo de trámite en el orden de la planilla (`secciones`, que viene del backend).
 * Dentro de cada sección se respeta el orden en que vienen (más viejas primero).
 */
export function ordenarFilas(filas, secciones = []) {
    const ordenSeccion = new Map(secciones.map((s, i) => [s.ambito, i]));
    const rango = (f) => {
        const cat = ORDEN_CATEGORIAS.indexOf(f.categoria);
        if (cat >= 0) return cat;
        const sec = ordenSeccion.get(f.ambito);
        return ORDEN_CATEGORIAS.length + (sec === undefined ? secciones.length : sec);
    };
    return filas
        .map((f, i) => ({ f, i }))
        .sort((a, b) => rango(a.f) - rango(b.f) || a.i - b.i)
        .map(({ f }) => f);
}

/**
 * Los filtros del listado sobre las filas ya traídas. `estadoAnses` acepta además SIN_CONTROL
 * (nunca se controló) y REQUIERE_CONTROL. La fecha de ingreso a ANSES se compara como texto
 * AAAA-MM-DD; sin fecha de ingreso, una fila no entra en un filtro de fechas.
 */
export function filtrarFilas(filas, { seccion = '', tipo = '', desde = '', hasta = '', estadoAnses = '', soloSeguidos = false } = {}) {
    return filas.filter((f) => {
        if (soloSeguidos && !f.seguido) return false;
        if (seccion && f.ambito !== seccion) return false;
        if (tipo && f.tipoTramite !== tipo) return false;
        if (estadoAnses === 'SIN_CONTROL' && f.ultimoControl) return false;
        if (estadoAnses === 'REQUIERE_CONTROL' && !f.requiereControl) return false;
        if (ESTADOS_ANSES[estadoAnses] && f.estadoAnses !== estadoAnses) return false;
        if (desde || hasta) {
            const ingreso = f.fechaIngresoAnses ? String(f.fechaIngresoAnses).slice(0, 10) : '';
            if (!ingreso) return false;
            if (desde && ingreso < desde) return false;
            if (hasta && ingreso > hasta) return false;
        }
        return true;
    });
}

/** El bloque al que va una fila: su sección destacada o, si no tiene, la de su tipo de trámite. */
export function grupoDeFila(fila) {
    const cat = CATEGORIAS[fila.categoria];
    if (cat) {
        return { clave: `cat-${fila.categoria}`, etiqueta: cat.etiqueta, color: cat.color, colorTexto: cat.colorTexto };
    }
    const etiqueta = fila.grupo || 'Sin clasificar';
    return { clave: `sec-${fila.ambito || etiqueta}`, etiqueta };
}

/** Qué acciones ofrece confirmar según el resultado. La primera es la sugerida. */
export function accionesDeConfirmacion(resultado) {
    switch (resultado) {
        case 'FAVORABLE':
            return [
                { valor: 'PASAR_A_FAVORABLE', etiqueta: 'Pasar a Favorables (cierra el trámite como favorable y crea el cobro)' },
                { valor: 'SIGUE_EN_LISTADO', etiqueta: 'Todavía no: queda en Para aprobar' },
            ];
        case 'DESFAVORABLE':
            return [
                { valor: 'SIGUE_EN_LISTADO', etiqueta: 'Sigue en Para aprobar (apelación, amparo o reclamo)' },
                { valor: 'CERRAR_DESFAVORABLE', etiqueta: 'Cerrar el trámite como desfavorable' },
            ];
        case 'CITADO':
            return [
                { valor: 'CARGAR_CITACION', etiqueta: 'Cargar la citación de ANSES (abre la ficha del trámite)' },
                { valor: 'SIGUE_EN_LISTADO', etiqueta: 'Ya está resuelto: solo confirmar' },
            ];
        case 'DESAPARECIDO':
            return [
                { valor: 'SIGUE_EN_LISTADO', etiqueta: 'Ya lo revisé en Mi ANSES: queda en Para aprobar' },
            ];
        default:
            return [];
    }
}

/** "hace 3 días", "hoy", para la columna Último control. */
export function haceCuanto(fechaIso, ahora = new Date()) {
    if (!fechaIso) return null;
    const fecha = new Date(fechaIso);
    if (Number.isNaN(fecha.getTime())) return null;
    const dias = Math.floor((ahora - fecha) / 86400000);
    if (dias <= 0) return 'hoy';
    if (dias === 1) return 'ayer';
    return `hace ${dias} días`;
}


/**
 * Muchas presentaciones guardan el expediente solo con dígitos ("0242717843510397401"). Se muestra
 * con los guiones de ANSES (024-27-17843510-3-974-01), que es como se lee y se dicta. Cualquier otro
 * formato se muestra tal cual.
 */
export function formatoExpediente(nro) {
    const d = String(nro || '').trim();
    if (!/^024\d{15,}$/.test(d)) return d;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5, 13)}-${d.slice(13, 14)}-${d.slice(14, 17)}-${d.slice(17)}`;
}
